import { useState, useEffect, useCallback, useRef } from "react";
import { Lock, Eye, EyeOff, Loader2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface PinStatus {
  requirePin: boolean;
  hasPin: boolean;
  hasActiveShare: boolean;
  autoLockMinutes: number;
  allowRememberDevice: boolean;
}

interface CarePinGateProps {
  hubId: string;
  children: React.ReactNode;
}

const STORAGE_KEY_PREFIX = "carePin_";

function getStoredToken(hubId: string): string | null {
  try {
    const key = STORAGE_KEY_PREFIX + hubId;
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStoredToken(hubId: string, token: string, persist: boolean) {
  try {
    const key = STORAGE_KEY_PREFIX + hubId;
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
    (persist ? localStorage : sessionStorage).setItem(key, token);
  } catch {}
}

function clearStoredToken(hubId: string) {
  try {
    const key = STORAGE_KEY_PREFIX + hubId;
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {}
}

export default function CarePinGate({ hubId, children }: CarePinGateProps) {
  const [pinStatus, setPinStatus] = useState<PinStatus | null>(null);
  const [checking, setChecking] = useState(true);
  const [unlocked, setUnlocked] = useState(false);

  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const initialCheckCompleteRef = useRef(false);

  const checkAccess = useCallback(async () => {
    if (!hubId) return;
    const isInitialCheck = !initialCheckCompleteRef.current;
    if (isInitialCheck) setChecking(true);

    try {
      // 1. Fetch PIN status
      const statusRes = await fetch(`/api/care/pin-status?hubId=${encodeURIComponent(hubId)}`);
      if (!statusRes.ok) {
        if (isInitialCheck) {
          setUnlocked(false);
          setError("Unable to verify Care View access. Please refresh and try again.");
        }
        return;
      }
      const status: PinStatus = await statusRes.json();
      setPinStatus(status);

      if (!status.hasPin && !status.hasActiveShare) {
        setUnlocked(false);
        return;
      }

      // 2. Check stored trusted token
      const storedToken = getStoredToken(hubId);
      if (storedToken) {
        const tokenRes = await fetch("/api/care/check-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hubId, token: storedToken }),
        });
        if (tokenRes.ok) {
          const data = await tokenRes.json();
          if (data.valid) {
            setUnlocked(true);
            return;
          }
        }
        clearStoredToken(hubId);
      }

      // PIN required, not trusted. This is an actual access failure, so a
      // background recheck may lock the view; merely checking must not.
      setUnlocked(false);
    } catch {
      // Keep an already-unlocked view mounted during transient background
      // failures. The next scheduled check will retry without resetting tabs
      // or scroll position. Initial access still fails closed.
      if (isInitialCheck) {
        setUnlocked(false);
        setError("Unable to verify Care View access. Please check your connection and try again.");
      }
    } finally {
      if (isInitialCheck) {
        initialCheckCompleteRef.current = true;
        setChecking(false);
      }
    }
  }, [hubId]);

  useEffect(() => {
    checkAccess();

    const recheckOnFocus = () => checkAccess();
    const recheckOnVisibility = () => {
      if (document.visibilityState === "visible") checkAccess();
    };
    const interval = window.setInterval(checkAccess, 30_000);

    window.addEventListener("focus", recheckOnFocus);
    document.addEventListener("visibilitychange", recheckOnVisibility);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", recheckOnFocus);
      document.removeEventListener("visibilitychange", recheckOnVisibility);
    };
  }, [checkAccess]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pin.trim() || submitting) return;
    setError("");
    setSubmitting(true);

    try {
      const res = await fetch("/api/care/verify-pin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hubId, pin: pin.trim(), rememberDevice }),
      });

      if (res.status === 429) {
        setError("Too many attempts. Please wait a minute and try again.");
        setSubmitting(false);
        return;
      }

      const data = await res.json();
      if (data.success) {
        if (data.trustedToken) {
          setStoredToken(
            hubId,
            data.trustedToken,
            rememberDevice && !!pinStatus?.allowRememberDevice,
          );
        }
        setUnlocked(true);
      } else {
        setError(data.error || "Incorrect PIN. Please try again.");
        setPin("");
      }
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Still checking
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Unlocked — show children
  if (unlocked) {
    return <>{children}</>;
  }

  if (pinStatus && !pinStatus.hasPin && !pinStatus.hasActiveShare) {
    return (
      <div data-testid="care-pin-setup-required" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-6 w-fit rounded-full bg-indigo-100 p-5">
            <Lock className="h-10 w-10 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Set up a Care View PIN</h1>
          <p className="text-sm text-slate-500 mb-6 leading-relaxed">
            Care View requires a PIN. Set it once and use the same PIN for all of your Hubs.
          </p>
          <Button asChild className="w-full h-12 bg-indigo-600 hover:bg-indigo-700">
            <a href={`/hub/${encodeURIComponent(hubId)}/settings/simple?tab=security`}>
              <Settings className="h-4 w-4 mr-2" />
              Open Profile Security Settings
            </a>
          </Button>
        </div>
      </div>
    );
  }

  // PIN required screen
  return (
    <div data-testid="care-pin-gate" className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4">
      <div className="w-full max-w-sm">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-indigo-100 p-5">
            <Lock className="h-10 w-10 text-indigo-600" />
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold text-center text-slate-800 mb-2">
          Private Care View
        </h1>
        <p className="text-center text-slate-500 text-sm mb-8 leading-relaxed">
          This page contains private resources for a care recipient.
          Please enter the PIN to continue.
        </p>

        {/* PIN form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              data-testid="care-pin-input"
              type={showPin ? "text" : "password"}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={pin}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                setPin(val);
                setError("");
              }}
              placeholder="Enter PIN"
              className="text-center text-2xl tracking-[0.6em] h-14 pr-12 border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl"
              autoComplete="off"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPin((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              tabIndex={-1}
            >
              {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-600 text-center font-medium">{error}</p>
          )}

          {pinStatus?.allowRememberDevice && (
            <div className="flex items-center gap-2 py-1">
              <Checkbox
                id="remember"
                checked={rememberDevice}
                onCheckedChange={(v) => setRememberDevice(!!v)}
              />
              <Label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                Remember this device
              </Label>
            </div>
          )}

          <Button
            data-testid="care-pin-submit"
            type="submit"
            disabled={pin.length < 4 || submitting}
            className="w-full h-12 text-base font-semibold bg-indigo-600 hover:bg-indigo-700 rounded-xl"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Unlock
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-8 space-y-2 text-center text-xs leading-relaxed">
          <p className="text-slate-400">
            If you need access, please contact the caregiver who shared this link.
          </p>
          <p className="text-slate-500">
            If you are the hub owner,{" "}
            <a
              data-testid="care-pin-settings-link"
              href={`/hub/${encodeURIComponent(hubId)}/settings/simple?tab=security`}
              className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700"
            >
              please set up your PIN
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
